// Globals
import debounce from 'lodash.debounce';
import { ChangeEvent, memo, ReactNode, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getProperties, selectAppliedSoldFilters, applySoldFilters, resetState } from '../../feature';

// Components
import {
  StyledFilterRow,
  StyledInput,
  StyledNumberInput,
  StyledSelect,
} from '../PropertiesFiltersContainer/PropertiesFiltersContainerStyles';

// Helpers
import { GetPropertiesDTO } from '../../dtos';
import { getPropertyTypeOptions, PropertyFiltersFields } from '../../helpers';

type IPropertiesSoldFiltersContainerProps = {
  children?: ReactNode;
  className?: string;
};

const PropertiesSoldFiltersContainer = ({ children, className }: IPropertiesSoldFiltersContainerProps) => {
  const { t } = useTranslation(['order', 'common']);
  const dispatch = useAppDispatch();
  const filters = useAppState(selectAppliedSoldFilters);

  const handleDebounceSearch = useCallback(
    debounce(async (searchParams) => {
      const dto = new GetPropertiesDTO(searchParams);
      dispatch(getProperties({ ...dto, isSold: true }));
    }, 500),
    [dispatch],
  );

  const handleSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      const newQuery = { ...filters, [name]: value || undefined };

      dispatch(applySoldFilters(newQuery));
      handleDebounceSearch(newQuery);
    },
    [filters, dispatch, handleDebounceSearch],
  );

  const handleSelect = useCallback(
    (name: string) => (value: unknown) => {
      const newQuery = {
        ...filters,
        [name]: value,
      };
      dispatch(applySoldFilters(newQuery));
      handleDebounceSearch(newQuery);
    },
    [filters, dispatch, handleDebounceSearch],
  );

  useEffect(() => {
    return () => {
      dispatch(resetState());
      handleDebounceSearch.cancel();
    };
  }, [dispatch, handleDebounceSearch]);

  const handleChangeOrderSum = useCallback(
    (value: number | null | string) => {
      const newQuery = {
        ...filters,
        [PropertyFiltersFields.ORDERS_SUM]: value?.toString(),
      };
      dispatch(applySoldFilters(newQuery));
      handleDebounceSearch(newQuery);
    },
    [filters, dispatch, handleDebounceSearch],
  );

  const handleChangeValuationPrice = useCallback(
    (value: number | null | string) => {
      const newQuery = {
        ...filters,
        [PropertyFiltersFields.VALUATION_PRICE]: value?.toString(),
      };
      dispatch(applySoldFilters(newQuery));
      handleDebounceSearch(newQuery);
    },
    [filters, dispatch, handleDebounceSearch],
  );

  const handleChangeSoldPrice = useCallback(
    (value: number | null | string) => {
      const newQuery = {
        ...filters,
        [PropertyFiltersFields.SOLD_PRICE]: value?.toString(),
      };
      dispatch(applySoldFilters(newQuery));
      handleDebounceSearch(newQuery);
    },
    [filters, dispatch, handleDebounceSearch],
  );

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th />
        <th>
          <StyledInput
            onChange={handleSearch}
            name={PropertyFiltersFields.ADDRESS}
            placeholder={t('common_search', { ns: 'common' })}
            allowClear
            size="middle"
            value={filters?.[PropertyFiltersFields.ADDRESS]}
          />
        </th>
        <th />
        <th />
        <th />
        <th>
          <StyledSelect
            onChange={handleSelect(PropertyFiltersFields.TYPE)}
            options={getPropertyTypeOptions(t)}
            suffixIcon={null}
            size="middle"
            placeholder={t('order_type')}
            isMultiselect
            allowClear
            maxTagCount={1}
            optionLabelProp="label"
            value={filters?.[PropertyFiltersFields.TYPE]}
          />
        </th>
        <th>
          <StyledInput
            onChange={handleSearch}
            placeholder={t('common_search', { ns: 'common' })}
            name={PropertyFiltersFields.EMAIL}
            size="middle"
            allowClear
            value={filters?.[PropertyFiltersFields.EMAIL]}
          />
        </th>
        <th>
          <StyledInput
            onChange={handleSearch}
            placeholder={t('common_search', { ns: 'common' })}
            name={PropertyFiltersFields.OWNERS}
            size="middle"
            allowClear
            value={filters?.[PropertyFiltersFields.OWNERS]}
          />
        </th>
        <th>
          <StyledInput
            onChange={handleSearch}
            name={PropertyFiltersFields.PHONE_NUMBER}
            placeholder={t('common_search', { ns: 'common' })}
            allowClear
            size="middle"
            value={filters?.[PropertyFiltersFields.PHONE_NUMBER]}
          />
        </th>
        <th />
        <th>
          <StyledNumberInput
            onChange={handleChangeValuationPrice}
            name={PropertyFiltersFields.VALUATION_PRICE}
            placeholder={t('common_search_more_than', { ns: 'common' })}
            controls={false}
            size="middle"
            value={
              filters?.[PropertyFiltersFields.VALUATION_PRICE]
                ? Number(filters[PropertyFiltersFields.VALUATION_PRICE])
                : undefined
            }
          />
        </th>
        <th>
          <StyledNumberInput
            onChange={handleChangeSoldPrice}
            name={PropertyFiltersFields.SOLD_PRICE}
            placeholder={t('common_search_more_than', { ns: 'common' })}
            controls={false}
            size="middle"
            value={
              filters?.[PropertyFiltersFields.SOLD_PRICE]
                ? Number(filters[PropertyFiltersFields.SOLD_PRICE])
                : undefined
            }
          />
        </th>
        <th>
          <StyledNumberInput
            onChange={handleChangeOrderSum}
            name={PropertyFiltersFields.ORDERS_SUM}
            placeholder={t('common_search_more_than', { ns: 'common' })}
            controls={false}
            size="middle"
            value={
              filters?.[PropertyFiltersFields.ORDERS_SUM]
                ? Number(filters[PropertyFiltersFields.ORDERS_SUM])
                : undefined
            }
          />
        </th>
      </StyledFilterRow>
    </thead>
  );
};

export default memo(PropertiesSoldFiltersContainer);
